import React, { useCallback, useEffect, useState } from "react";
import { RiArrowLeftSLine } from "react-icons/ri";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";
import OwlCarousel from "react-owl-carousel";
import Rating from "@mui/material/Rating";
import "owl.carousel/dist/assets/owl.theme.default.css";

import Footer from "../components/Footer";
import LoggedNav from "../components/LoggedNav";
import PopUp from "../components/Popup";
import Spinner from "./BePatient";
import LinkPayment from "./LinkPayment";
import DeletePaymentMethod from "./DeletePaymentMethod";

import profile from "../images/Vector.jpg";

const PAGE_META = {
  title: "Profile | CHEF | CHEF REPUBLIC",
  description:
    "View Chef Profile in Detail including the menu, available timings, learn about the Chef, get to know the chef by viewing their pics",
};

const WEEKDAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const extractValues = (arr, prop) => arr.map((item) => item[prop]);

const formatDayLabel = (day) =>
  day.charAt(0).toUpperCase() + day.slice(1, 3) + ":";

function BrowseChef(props) {
  const navigate = useNavigate();

  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [isPaymentLinked, setIsPaymentLinked] = useState(false);
  const [shouldPollSyncStatus, setShouldPollSyncStatus] = useState(false);

  const role = localStorage.getItem("role");
  const token = localStorage.getItem("token");

  const fetchChefDetails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}user/loggedin_chef_detail`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            user_id: localStorage.getItem("logged_user_id") || "",
            email: localStorage.getItem("owner") || "",
          }),
        }
      );
      const parsed = await res.json();
      setDetails(parsed.data);
    } catch (err) {
      console.error("Failed to fetch chef details:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkPaymentStatus = useCallback(async (showLoader = false) => {
    if (showLoader) setPaymentLoading(true);
    try {
      const userId = localStorage.getItem("logged_user_id");
      const authToken = localStorage.getItem("token");

      const accountRes = await fetch(
        `${process.env.REACT_APP_BASE_URL}user/get_chef_account_details`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ user_id: userId }),
        }
      );
      const accountData = await accountRes.json();

      const emailStatus = String(accountData.emailStatus || "")
        .replace(/[\s_]/g, "")
        .toUpperCase();

      if (accountData.status === true && emailStatus === "NOTCONFIRMED") {
        setIsPaymentLinked(true);
        setShouldPollSyncStatus(true);

        const syncRes = await fetch(
          `${process.env.REACT_APP_BASE_URL}user/syncChefPayPalStatus`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${authToken}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ userId }),
          }
        );
        const syncData = await syncRes.json();

        const isReady =
          syncData.success &&
          syncData.primary_email_confirmed === true &&
          syncData.payments_receivable === true;

        setShouldPollSyncStatus(!isReady);
        setIsPaymentLinked(true);
      } else if (accountData.status === true && emailStatus === "CONFIRMED") {
        setShouldPollSyncStatus(false);
        setIsPaymentLinked(true);
      } else {
        setShouldPollSyncStatus(false);
        setIsPaymentLinked(false);
      }
    } catch (err) {
      console.error("Payment status check failed:", err);
      setIsPaymentLinked(false);
    } finally {
      if (showLoader) setPaymentLoading(false);
    }
  }, []);

  useEffect(() => {
    document.title = PAGE_META.title;
    document.querySelector("meta[name='title']")?.setAttribute("content", PAGE_META.title);
    document.querySelector("meta[name='description']")?.setAttribute("content", PAGE_META.description);
    document.querySelector("meta[property='og:title']")?.setAttribute("content", PAGE_META.title);
    document.querySelector("meta[property='og:description']")?.setAttribute("content", PAGE_META.description);
    document.querySelector("meta[property='twitter:title']")?.setAttribute("content", PAGE_META.title);
    document.querySelector("meta[property='twitter:description']")?.setAttribute("content", PAGE_META.description);

    if (!role || role !== "2" || !token) {
      navigate("/chef/sign-in");
      return;
    }

    fetchChefDetails();
    checkPaymentStatus();
  }, [role, token, navigate, fetchChefDetails, checkPaymentStatus]);

  useEffect(() => {
    if (!shouldPollSyncStatus) return;

    const intervalId = setInterval(checkPaymentStatus, 5000);
    return () => clearInterval(intervalId);
  }, [shouldPollSyncStatus, checkPaymentStatus]);

  const handleNavigateEdit = () => navigate("/chef/profile/edit");

  const handleLogout = () => {
    localStorage.clear();
    navigate("/chef");
  };

  const handlePaymentDeleted = () => {
    checkPaymentStatus(true);
    fetchChefDetails();
  };

  if (loading || paymentLoading) return <Spinner />;
  if (!details.length) return null;

  const chef = details[0];

  return (
    <>
      <LoggedNav customer={props.customer} />

      <div className="container mt-5">
        {!isMobile && (
          <nav aria-label="breadcrumb">
            <a
              href="/chef/order-list"
              style={{ textDecoration: "none", color: "black", fontWeight: 500 }}
            >
              <RiArrowLeftSLine size="2rem" />
              <span style={{ color: "#1e1e1e", fontWeight: 400 }}>Order List</span>
            </a>
          </nav>
        )}

        <div
          className="main_container row mt-4"
          style={{ display: "flex", flexDirection: isMobile ? "column" : "row" }}
        >
          {/* Sidebar */}
          <div className="col-4 vstack prof pe-4">
            <OwlCarousel
              items={1}
              center
              mouseDrag
              touchDrag
              autoplay
              autoplayHoverPause
              dotsEach
              loop
              className="owl-theme mb-4 shadow rounded-4 p-2"
            >
              <div className="d-flex justify-content-center align-items-center">
                <img
                  alt={chef.chef_name}
                  className="image_box img-fluid"
                  src={chef.chef_profile_image}
                  style={{ maxHeight: "15rem", width: "auto", maxWidth: "90%" }}
                />
              </div>
              {chef.chef_images.map((src, index) => (
                <div
                  key={index}
                  className="d-flex justify-content-center align-items-center"
                >
                  <img
                    alt={`${chef.chef_name} gallery ${index + 1}`}
                    className="image_box img-fluid"
                    src={src}
                    style={{ maxHeight: "15rem", width: "auto", maxWidth: "90%" }}
                  />
                </div>
              ))}
            </OwlCarousel>

            <button
              type="button"
              className="btn btn-orange rounded-pill mb-3"
              onClick={handleNavigateEdit}
            >
              Edit Profile
            </button>

            <LinkPayment
              isPaymentLinked={isPaymentLinked}
              onAddChefAccountSuccess={() => {
                setPaymentLoading(true);
                checkPaymentStatus(true);
              }}
            />

            <DeletePaymentMethod
              isPaymentLinked={isPaymentLinked}
              onPaymentDeleted={handlePaymentDeleted}
            />

            <button
              type="button"
              className="btn btn-orange rounded-pill mb-3"
              onClick={() => navigate("/chef/order-list")}
            >
              Order List
            </button>

            <button
              type="button"
              className="btn btn-orange rounded-pill mb-3"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>

          {/* Main Content */}
          <div className="col-8 py-3">
            <div className="box_title">
              <h4 className="fw-bold">
                {chef.chef_name}
                {chef.chef_sex ? `, ${chef.chef_sex}` : ""}
              </h4>
              <h6 className="mt-3">{chef.chef_city}</h6>
            </div>

            <div className="d-flex justify-content-start pt-3 mt-3">
              <Rating
                name="chef-rating"
                defaultValue={Number(chef.chef_rating)}
                precision={0.5}
                readOnly
              />
              <p className="ms-3" style={{ fontSize: "0.9rem", color: "#7C7C7C" }}>
                {chef.chef_total_number_of_reviews} reviews
              </p>
            </div>

            <hr />

            <div className="message_box my-2 py-2">
              <h5 className="my-2" style={{ fontWeight: 700 }}>
                I can cook
              </h5>
              <h6 className="mt-2">
                {chef.chef_categories
                  ? extractValues(chef.chef_categories, "category_name").join(", ")
                  : ""}
              </h6>
            </div>

            <button
              type="button"
              className="btn-orange"
              data-bs-toggle="modal"
              data-bs-target="#staticBackdrop"
            >
              View my menu
            </button>
            <PopUp
              chef_name={chef.chef_name}
              rich_text_menu={chef.rich_text_menu}
              pdf={chef.chef_menu}
            />

            <hr />

            <div className="message_box my-2 py-2">
              <h5 className="my-2 pb-2" style={{ fontWeight: 700 }}>
                My Availability
              </h5>
              <div className="row d-flex justify-content-between">
                {WEEKDAYS.map((day) => {
                  const slots = chef.chef_availability?.[day];
                  if (!slots?.length) return null;
                  return (
                    <div className="col-6 row my-1" key={day}>
                      <p className="col-3" style={{ fontSize: "0.9rem", fontWeight: 700 }}>
                        {formatDayLabel(day)}
                      </p>
                      <p className="col-9 p-0" style={{ fontSize: "0.9rem" }}>
                        {slots.join("; ")}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            <hr />

            <div className="message_box my-4">
              <h5 className="my-2 py-2" style={{ fontWeight: 700 }}>
                About me
              </h5>
              <p className="my-2">{chef.chef_description}</p>
            </div>

            <hr />

            <div className="message_box my-4">
              <h5 className="my-2" style={{ fontWeight: 700 }}>
                Client Reviews
              </h5>
              {chef.chef_reviews?.length > 0 ? (
                <OwlCarousel
                  items={1}
                  mouseDrag
                  touchDrag
                  autoplay
                  autoplayHoverPause
                  dotsEach
                  loop
                  className="owl-theme my-4"
                >
                  {chef.chef_reviews.map((review, index) => (
                    <div className="item" key={index}>
                      <Rating
                        name={`review-rating-${index}`}
                        defaultValue={Number(review.rating)}
                        precision={0.5}
                        readOnly
                      />
                      <p
                        className="mt-2 text-wrap text-break"
                        style={{ fontSize: "0.9rem" }}
                      >
                        {review.review_content}
                      </p>
                      <div className="d-flex justify-content-start align-items-center">
                        <img
                          src={review.user_image || profile}
                          className="rounded-pill"
                          style={{ width: "35px", height: "35px" }}
                          alt={review.reviewed_by}
                        />
                        <p
                          style={{
                            marginLeft: "15px",
                            marginBottom: "1px",
                            fontSize: "0.9rem",
                          }}
                        >
                          {review.reviewed_by}
                        </p>
                      </div>
                    </div>
                  ))}
                </OwlCarousel>
              ) : (
                <p className="my-2">No Review Yet</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer top={1} />
    </>
  );
}

export default BrowseChef;
